import {
  ComercializadorSchema,
  ConsultaDeCertificadosSchema,
  ListaDeCertificadosSchema,
  OpcionesDeColumnaSchema,
  type ConsultaDeCertificados,
  PanelDeCertificadosSchema,
  PropuestaDeExportacionSchema,
  PropuestaDeRecoleccionSchema,
  ResultadoDeCargaDeCertificadosSchema,
  ResultadoDeCertificadosDeExportacionSchema,
  ResultadoDeCertificadosDeRecoleccionSchema,
  type PeticionDeCertificadosDeExportacion,
  ResultadoDeEliminarCertificadosSchema,
  ResultadoDeCertificadoPorFueraSchema,
  type PeticionDeCertificadoPorFuera,
  PaqueteDeCertificadosSchema,
  type DocumentoDeOrigen,
  type PeticionDeEliminarCertificados,
  type PeticionDeCertificadosDeRecoleccion,
  type Comercializador,
  type PerfilDePlanta,
  type PerfilDeTitulo,
  type ResultadoDeCargaDeCertificados,
  type TipoDeCargaDeCertificados,
} from '@cnc/contracts';
import {keepPreviousData, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {tr} from '../i18n/idioma';
import {json, pedir} from './cliente';
import {aParametros} from './consulta';

// Certificados de origen y de analisis (decision 0067).
export function usePanelDeCertificados() {
  return useQuery({
    queryKey: ['certificados', 'panel'],
    queryFn: () => pedir('certificados/panel', PanelDeCertificadosSchema),
  });
}

// La consulta de la lista, con sus filtros (decision 0076); vive en la direccion.
export const CONSULTA_DE_CERTIFICADOS_POR_DEFECTO = ConsultaDeCertificadosSchema.parse({});
export const parametrosDeCertificados = (consulta: Partial<ConsultaDeCertificados>) =>
  aParametros(consulta, CONSULTA_DE_CERTIFICADOS_POR_DEFECTO);

export function useListaDeCertificados(consulta: ConsultaDeCertificados) {
  const cadena = aParametros(consulta, {}).toString();
  return useQuery({
    queryKey: ['certificados', 'lista', cadena],
    queryFn: () => pedir(`certificados?${cadena}`, ListaDeCertificadosSchema),
    placeholderData: keepPreviousData,
  });
}

// Valores de una columna para su filtro; solo se piden al abrir la lista.
export function useOpcionesDeColumnaDeCertificados(
  consulta: ConsultaDeCertificados,
  columna: string,
  contiene: string,
  habilitado: boolean,
) {
  const parametros = aParametros({...consulta, limite: undefined}, {});
  parametros.set('columna', columna);
  if (contiene) parametros.set('contiene', contiene);
  const cadena = parametros.toString();
  return useQuery({
    queryKey: ['certificados', 'opciones-de-columna', cadena],
    queryFn: () => pedir(`certificados/opciones-de-columna?${cadena}`, OpcionesDeColumnaSchema),
    enabled: habilitado,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

function useCambio<A, R>(fn: (a: A) => Promise<R>) {
  const cliente = useQueryClient();
  return useMutation({mutationFn: fn, onSettled: () => cliente.invalidateQueries({queryKey: ['certificados']})});
}

export const useGuardarPerfilDeTitulo = () =>
  useCambio(({id, perfil}: {id: number; perfil: PerfilDeTitulo}) =>
    pedir(`certificados/titulos/${id}/perfil`, PanelDeCertificadosSchema, json('PUT', {perfil})),
  );

export const useGuardarPerfilDePlanta = () =>
  useCambio(({id, perfil}: {id: number; perfil: PerfilDePlanta}) =>
    pedir(`certificados/plantas/${id}/perfil`, PanelDeCertificadosSchema, json('PUT', {perfil})),
  );

export const useGuardarComercializador = () =>
  useCambio((datos: Comercializador) =>
    pedir('certificados/comercializador', ComercializadorSchema, json('PUT', datos)),
  );

export const useCargarCertificados = () =>
  useCambio(
    ({tipo, archivos, modo}: {tipo: TipoDeCargaDeCertificados; archivos: File[]; modo: 'revisar' | 'aplicar'}) => {
      const cuerpo = new FormData();
      for (const a of archivos) cuerpo.append('archivos', a, a.name);
      cuerpo.append('tipo', tipo);
      cuerpo.append('modo', modo);
      return pedir('certificados/cargar', ResultadoDeCargaDeCertificadosSchema, {method: 'POST', body: cuerpo});
    },
  );

// Certificados de analisis de Colombian Mint de la carpeta Recolecciones
// (decision 0100): de todo lo elegido, solo los Word y PDF de certificados de
// analisis desde 2026 que no son de Mona Minas (MM, S MM, BV, S BV, MV, MS,
// RM). Van por partes, cada Word con su PDF, y los resultados se suman.
const MONA_MINAS = /^(S[\s-]*)?(MM|BV|MV|MS|RM)$/i;
const DESDE = new Date('2026-01-01T00:00:00').getTime();
const partesDelAnalisis = (nombre: string) => {
  const limpio = nombre.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
  const m = /^CERTIFICADO DE ANALISIS(?: INTERNO)?\s+(.*?)\s*\bS\s*-?\s*\d{2,4}/.exec(limpio);
  return {esAnalisis: /^CERTIFICADO DE ANALISIS/.test(limpio), prefijo: m?.[1]?.trim() ?? null};
};
export function certificadosDeColombianMint(archivos: File[]): File[] {
  return archivos.filter(a => {
    if (!/\.(docx|pdf)$/i.test(a.name) || a.name.startsWith('~$') || a.lastModified < DESDE) return false;
    const {esAnalisis, prefijo} = partesDelAnalisis(a.name);
    return esAnalisis && !(prefijo && MONA_MINAS.test(prefijo));
  });
}

const POR_LOTE = 20;
export async function cargarAnalisisPorLotes(
  archivos: File[],
  modo: 'revisar' | 'aplicar',
  alAvanzar: (hechos: number, total: number) => void,
): Promise<ResultadoDeCargaDeCertificados> {
  // Cada Word junto con su PDF (mismo nombre).
  const base = (n: string) => n.replace(/\.(docx|pdf)$/i, '').toUpperCase();
  const pares = new Map<string, File[]>();
  for (const a of archivos) pares.set(base(a.name), [...(pares.get(base(a.name)) ?? []), a]);
  const lotes: File[][] = [[]];
  for (const par of pares.values()) {
    if (lotes.at(-1)!.length + par.length > POR_LOTE) lotes.push([]);
    lotes.at(-1)!.push(...par);
  }
  let total: ResultadoDeCargaDeCertificados | null = null;
  for (const [i, lote] of lotes.entries()) {
    if (!lote.some(a => /\.docx$/i.test(a.name))) continue;
    const cuerpo = new FormData();
    for (const a of lote) cuerpo.append('archivos', a, a.name);
    cuerpo.append('tipo', 'analisis');
    cuerpo.append('modo', modo);
    const r = await pedir('certificados/cargar', ResultadoDeCargaDeCertificadosSchema, {method: 'POST', body: cuerpo});
    total = total ? sumarResultados(total, r) : r;
    alAvanzar(i + 1, lotes.length);
  }
  if (!total) throw new Error('sin archivos');
  const a = total.analisis;
  const resumen = tr(
    `Certificados de análisis: ${a.nuevos} nuevos, ${a.cambian} cambian, ${a.iguales} iguales. Big Bags ligados: ${total.bigBagsEnlazados}.`,
    `Analysis certificates: ${a.nuevos} new, ${a.cambian} change, ${a.iguales} unchanged. Big Bags linked: ${total.bigBagsEnlazados}.`,
  );
  return {...total, mensaje: total.aplicado ? tr(`Guardado. ${resumen}`, `Saved. ${resumen}`) : resumen};
}

function sumarResultados(x: ResultadoDeCargaDeCertificados, y: ResultadoDeCargaDeCertificados) {
  const conteo = (a: typeof x.analisis, b: typeof x.analisis) => ({
    nuevos: a.nuevos + b.nuevos,
    cambian: a.cambian + b.cambian,
    iguales: a.iguales + b.iguales,
    protegidos: a.protegidos + b.protegidos,
  });
  return {
    ...x,
    aplicado: x.aplicado && y.aplicado,
    archivos: [...x.archivos, ...y.archivos],
    origen: conteo(x.origen, y.origen),
    analisis: conteo(x.analisis, y.analisis),
    bigBagsEnlazados: x.bigBagsEnlazados + y.bigBagsEnlazados,
    sinEncontrar: [...new Set([...x.sinEncontrar, ...y.sinEncontrar])],
    avisos: [...new Set([...x.avisos, ...y.avisos])],
  };
}

// Leer las facturas en PDF que ya estan en los expedientes (decision 0068).
export const useLeerFacturasDeExpedientes = () =>
  useCambio((modo: 'revisar' | 'aplicar') =>
    pedir('certificados/facturas/leer-expedientes', ResultadoDeCargaDeCertificadosSchema, json('POST', {modo})),
  );

export const RUTA_RELACION_POR_BIG_BAG = '/api/certificados/relacion-por-big-bag';

// ── Generar (decision 0072) ─────────────────────────────────────────────────

export const rutaDeDocumentoDeOrigen = (id: number, documento: DocumentoDeOrigen) =>
  `/api/certificados/origen/${id}/archivo?documento=${documento}`;
export const rutaDeDocumentoDeAnalisis = (id: number, formato: 'word' | 'pdf' = 'word') =>
  `/api/certificados/analisis/${id}/archivo${formato === 'pdf' ? '?formato=pdf' : ''}`;

export function usePropuestaDeRecoleccion(recoleccion: string | null) {
  return useQuery({
    queryKey: ['certificados', 'recoleccion', recoleccion],
    queryFn: () =>
      pedir(`certificados/recolecciones/${encodeURIComponent(recoleccion!)}`, PropuestaDeRecoleccionSchema),
    enabled: Boolean(recoleccion),
    retry: false,
  });
}

// Paquete de certificados de una exportacion (decision 0075).
export function usePaqueteDeCertificados(exportacion: string | null) {
  return useQuery({
    queryKey: ['certificados', 'paquete', exportacion],
    queryFn: () =>
      pedir(`certificados/exportaciones/${encodeURIComponent(exportacion!)}/paquete`, PaqueteDeCertificadosSchema),
    enabled: Boolean(exportacion),
    retry: false,
  });
}
export function rutaDelPaqueteDeCertificados(exportacion: string, fecha: string, radicado: string) {
  const p = new URLSearchParams();
  if (fecha) p.set('fecha', fecha);
  if (radicado.trim()) p.set('radicado', radicado.trim());
  const consulta = p.toString();
  return `/api/certificados/exportaciones/${encodeURIComponent(exportacion)}/paquete/archivo${consulta ? `?${consulta}` : ''}`;
}

// Eliminar los certificados de una recoleccion o de una exportacion (decision 0074).
export const useEliminarCertificados = () =>
  useCambio((peticion: PeticionDeEliminarCertificados) =>
    pedir('certificados/eliminar', ResultadoDeEliminarCertificadosSchema, json('POST', peticion)),
  );

// Un certificado hecho por fuera de la app (decision 0097).
export const useRegistrarCertificadoPorFuera = () =>
  useCambio((peticion: PeticionDeCertificadoPorFuera) =>
    pedir('certificados/por-fuera', ResultadoDeCertificadoPorFueraSchema, json('POST', peticion)),
  );

export const useCrearCertificadosDeRecoleccion = () =>
  useCambio(({recoleccion, peticion}: {recoleccion: string; peticion: PeticionDeCertificadosDeRecoleccion}) =>
    pedir(
      `certificados/recolecciones/${encodeURIComponent(recoleccion)}`,
      ResultadoDeCertificadosDeRecoleccionSchema,
      json('POST', peticion),
    ),
  );

// Todos los certificados que le faltan a una exportacion (decision 0088).
export function usePropuestaDeExportacion(exportacion: string | null) {
  return useQuery({
    queryKey: ['certificados', 'propuesta-exportacion', exportacion],
    queryFn: () =>
      pedir(`certificados/exportaciones/${encodeURIComponent(exportacion!)}/propuesta`, PropuestaDeExportacionSchema),
    enabled: Boolean(exportacion),
    retry: false,
  });
}

export const useCrearCertificadosDeExportacion = () =>
  useCambio(({exportacion, peticion}: {exportacion: string; peticion: PeticionDeCertificadosDeExportacion}) =>
    pedir(
      `certificados/exportaciones/${encodeURIComponent(exportacion)}/crear`,
      ResultadoDeCertificadosDeExportacionSchema,
      json('POST', peticion),
    ),
  );
